import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'
import { effectivePrice, overridesPayload, priceChanges, rowProblem, rowsFromServer, type OptionPriceRow } from '@/lib/optionOverrides'

/**
 * "Prices on this item" (F-MOD-N13).
 *
 * A shared option list — one "Hot or Iced" for every drink — priced per item:
 * blank uses the shared price, a typed price is this item's own, and an option
 * can be switched off for this item alone. Saves on its own button with a
 * before/after confirm, because it moves what guests pay (stop-and-ask), and it
 * never touches the shared list or any other item.
 */
export function ItemOptionPrices({
  productId,
  productName,
  formatCurrency,
}: {
  productId: string
  productName: string
  formatCurrency: (n: number) => string
}) {
  const queryClient = useQueryClient()
  const q = useQuery({
    queryKey: ['product-option-overrides', productId],
    queryFn: async () => {
      const res = await apiClient.getProductOptionOverrides(productId)
      if (!res.success) throw new Error(res.message)
      return res.data ?? []
    },
  })
  const baseline = useMemo(() => rowsFromServer(q.data ?? []), [q.data])
  const [rows, setRows] = useState<OptionPriceRow[]>([])
  useEffect(() => setRows(baseline), [baseline])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const changes = useMemo(() => priceChanges(baseline, rows, formatCurrency), [baseline, rows, formatCurrency])
  const problem = rows.map(rowProblem).find(Boolean) ?? null

  if (q.isLoading || rows.length === 0) return null

  const patch = (optionId: string, p: Partial<OptionPriceRow>) =>
    setRows((rs) => rs.map((r) => (r.optionId === optionId ? { ...r, ...p } : r)))

  const save = async () => {
    setSaving(true)
    try {
      const res = await apiClient.putProductOptionOverrides(productId, overridesPayload(rows))
      if (!res.success) throw new Error(res.message)
      toastHelpers.success('Option prices saved', `${productName} now charges its own prices.`)
      setConfirmOpen(false)
      queryClient.invalidateQueries({ queryKey: ['product-option-overrides', productId] })
      queryClient.invalidateQueries({ queryKey: ['menu-modifiers'] })
      queryClient.invalidateQueries({ queryKey: ['product-recipe', productId] })
    } catch (err) {
      toastHelpers.error('Option prices not saved', err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setSaving(false)
    }
  }

  // One block per list, in the order the cashier meets them.
  const groups: Array<{ id: string; name: string; shared: number; rows: OptionPriceRow[] }> = []
  for (const r of rows) {
    let g = groups.find((x) => x.id === r.groupId)
    if (!g) groups.push((g = { id: r.groupId, name: r.groupName, shared: r.groupProductCount, rows: [] }))
    g.rows.push(r)
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Option prices on this item</h3>
        <p className="text-xs text-muted-foreground">
          Leave a price blank to use the shared list’s price. A price typed here applies to {productName || 'this item'} only.
        </p>
      </div>
      {groups.map((g) => (
        <div key={g.id} className="rounded-md border">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-sm font-medium">{g.name}</span>
            {g.shared > 1 && <Badge variant="secondary">shared · {g.shared} items</Badge>}
          </div>
          <div className="divide-y">
            {g.rows.map((r) => {
              const eff = effectivePrice(r)
              return (
                <div key={r.optionId} className="grid grid-cols-[1fr_7rem_auto] items-center gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm">{r.optionName}</div>
                    <div className="text-xs text-muted-foreground">
                      Shared: {r.priceMode === 'replace' ? formatCurrency(r.sharedPrice) : `+${formatCurrency(r.sharedPrice)}`}
                      {!r.sharedAvailable && ' · sold out everywhere'}
                      {r.hasOwnRecipe && ' · own recipe'}
                    </div>
                  </div>
                  <Input
                    inputMode="decimal"
                    aria-label={`${r.optionName} price on this item`}
                    placeholder={String(r.sharedPrice)}
                    value={r.priceText}
                    onChange={(e) => patch(r.optionId, { priceText: e.target.value })}
                    className={eff === null ? 'border-destructive' : undefined}
                  />
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Switch
                      checked={r.offered}
                      disabled={!r.sharedAvailable}
                      onCheckedChange={(v) => patch(r.optionId, { offered: v })}
                      aria-label={`Offer ${r.optionName} on this item`}
                    />
                    Offered
                  </label>
                </div>
              )
            })}
          </div>
        </div>
      ))}
      {problem && <p className="text-xs text-destructive">{problem}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" disabled={changes.length === 0} onClick={() => setRows(baseline)}>
          Undo
        </Button>
        <Button type="button" size="sm" disabled={changes.length === 0 || Boolean(problem)} onClick={() => setConfirmOpen(true)}>
          Save option prices
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change what guests pay for {productName || 'this item'}?</DialogTitle>
            <DialogDescription>Only this item changes. The shared lists and every other item keep their prices.</DialogDescription>
          </DialogHeader>
          <ul className="space-y-1 text-sm">
            {changes.map((c, i) => (
              <li key={i} className="flex justify-between gap-4">
                <span className="truncate">{c.label}</span>
                <span className="whitespace-nowrap tabular-nums">
                  {c.before} → <strong>{c.after}</strong>
                </span>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
